import { useMemo } from 'react';
import {
  useAppSettingsDictionary,
  useAssetsIcons,
  useOperationsHistory,
} from '@/shared/api';
import {
  EXCHANGE_DISPLAYED_QUICK_SELECT_ITEMS_LIMIT,
  EXCHANGE_FILTERED_QUICK_SELECT_ITEMS_LIMIT,
} from '@/shared/config';
import type {
  ExchangeAsset as ExchangeAssetType,
  ExchangeInputSectionType,
  ExchangeQuickSelectItem,
} from './types';

type ExchangeQuickSelectData = Array<{
  asset: ExchangeQuickSelectItem;
}>;

const useExchangeQuickSelect = (
  type: ExchangeInputSectionType,
  assetsOptions: Array<ExchangeAssetType>
) => {
  const { assetsIconsData, isAssetsIconsLoading } = useAssetsIcons();
  const { appSettingsData, isAppSettingsLoading } = useAppSettingsDictionary();
  const {
    operationsHistory: exchangeHistory,
    isOperationsHistoryLoading: isExchangeHistoryLoading,
  } = useOperationsHistory('all');

  const exchangeQuickSelectData: ExchangeQuickSelectData = useMemo(() => {
    if (!exchangeHistory || !assetsIconsData || !appSettingsData) return [];

    const exchangeHistoryItems = exchangeHistory.operations
      .filter(operation => operation.operationType === 'exchange')
      .slice(0, EXCHANGE_FILTERED_QUICK_SELECT_ITEMS_LIMIT);

    const mapToItem = (from?: string, to?: string) => {
      const selectedCode = type === 'from' ? from : to;

      return {
        asset: {
          code: selectedCode ?? '',
          icon: assetsIconsData?.value[selectedCode ?? ''],
        },
      };
    };

    let items: ExchangeQuickSelectData;

    if (exchangeHistoryItems.length === 0) {
      const { recommendedExchangePairs } = appSettingsData.value;

      items = recommendedExchangePairs.map(pair =>
        mapToItem(pair.assetFrom, pair.assetTo)
      );
    } else {
      items = exchangeHistoryItems.map(({ asset, additionalAsset }) =>
        mapToItem(additionalAsset?.code, asset.code)
      );
    }

    return Array.from(
      new Map(
        items
          .filter(
            item =>
              item.asset.code &&
              assetsOptions.some(asset => asset.code === item.asset.code)
          )
          .map(item => [item.asset.code, item])
      ).values()
    ).slice(0, EXCHANGE_DISPLAYED_QUICK_SELECT_ITEMS_LIMIT);
  }, [exchangeHistory, appSettingsData, assetsIconsData, type, assetsOptions]);

  return {
    exchangeQuickSelectData,
    isExchangeQuickSelectLoading:
      isExchangeHistoryLoading || isAssetsIconsLoading || isAppSettingsLoading,
  };
};

export { useExchangeQuickSelect };
